import React, { useState, useMemo } from 'react';
import { Box, Button, Paper, Typography, Alert, AlertTitle, Chip, Tooltip } from '@mui/material';
import RulesInfoBox from './RulesInfoBox';
import AddIcon from '@mui/icons-material/Add';
import { RuleColumn } from './RuleColumn';
import { RuleModal } from './RuleModal';
import { Rule, RuleGroup, RuleKanbanData } from './types';
import { GiftRuleType } from '../../../../models';
import { v4 as uuidv4 } from 'uuid';
import GiftRulesSimulatorPanel from './GiftRulesSimulatorPanel';

interface RuleKanbanBoardProps {
  data: RuleKanbanData;
  onChange: (data: RuleKanbanData) => void;
  fuels: Array<{ id: string; name: string }>;
  stations: Array<{ id: string; name: string }>;
  chain?: any;
}

export const RuleKanbanBoard: React.FC<RuleKanbanBoardProps> = ({
  data,
  onChange,
  fuels,
  stations,
  chain,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentGroupIndex, setCurrentGroupIndex] = useState<number | null>(null);

  // Criar maps para lookup rápido
  const fuelsMap = new Map(fuels.map((f) => [f.id, f.name]));
  const stationsMap = new Map(stations.map((s) => [s.id, s.name]));

  // Verificar se existe regra "Clube Certo" em qualquer grupo
  const hasClubeCerto = useMemo(() => {
    return data.ruleGroups.some((group) =>
      group.rules.some((rule) => rule.type === GiftRuleType.clube_certo)
    );
  }, [data.ruleGroups]);

  // Verificar se existe regra "Pix Payment" em qualquer grupo
  const hasPixPayment = useMemo(() => {
    return data.ruleGroups.some((group) =>
      group.rules.some((rule) => rule.type === GiftRuleType.pix_payment)
    );
  }, [data.ruleGroups]);

  // Verificar se existe regra "Troca por Pontos" em qualquer grupo.
  // Exclusiva como a de Divulgação, e pelo mesmo motivo do backend: o preço é do
  // brinde inteiro, então qualquer outra regra mudaria o que está sendo vendido.
  const hasPointsExchange = useMemo(() => {
    return data.ruleGroups.some((group) =>
      group.rules.some((rule) => rule.type === GiftRuleType.points_exchange)
    );
  }, [data.ruleGroups]);

  // Verificar se existem regras dirigidas por evento (abastecimento ou compra).
  // As duas famílias convivem entre si, mas nenhuma convive com pix/unique_code —
  // por isso as regras de compra entram no mesmo guard das de abastecimento.
  const hasFillinRules = useMemo(() => {
    return data.ruleGroups.some((group) =>
      group.rules.some((rule) => GiftRuleType.isEventType(rule.type))
    );
  }, [data.ruleGroups]);

  // Verificar se existe regra "Código Único" em qualquer grupo (ferramenta de atribuição)
  const hasUniqueCode = useMemo(() => {
    return data.ruleGroups.some((group) =>
      group.rules.some((rule) => rule.type === GiftRuleType.unique_code)
    );
  }, [data.ruleGroups]);

  // Verificar se tem regra exclusiva (clube_certo, pix_payment, unique_code ou points_exchange)
  const hasExclusiveRule = hasClubeCerto || hasPixPayment || hasUniqueCode || hasPointsExchange;

  // Desabilitar adição de grupos se regra exclusiva está presente E já existe pelo menos 1 grupo
  const canAddGroup = !hasExclusiveRule || data.ruleGroups.length === 0;

  const handleAddGroup = () => {
    const newGroup: RuleGroup = {
      id: uuidv4(),
      rules: [],
    };
    onChange({
      ...data,
      ruleGroups: [...data.ruleGroups, newGroup],
    });
  };

  const handleRemoveGroup = (groupIndex: number) => {
    const newGroups = data.ruleGroups.filter((_, idx) => idx !== groupIndex);
    onChange({
      ...data,
      ruleGroups: newGroups,
    });
  };

  const handleAddRule = (groupIndex: number) => {
    setCurrentGroupIndex(groupIndex);
    setModalOpen(true);
  };

  const handleRemoveRule = (groupIndex: number, ruleIndex: number) => {
    const newGroups = [...data.ruleGroups];
    newGroups[groupIndex].rules = newGroups[groupIndex].rules.filter(
      (_, idx) => idx !== ruleIndex
    );
    onChange({
      ...data,
      ruleGroups: newGroups,
    });
  };

  const handleSaveRule = (rule: Rule) => {
    if (currentGroupIndex === null) return;

    const newGroups = [...data.ruleGroups];
    newGroups[currentGroupIndex].rules.push(rule);
    onChange({
      ...data,
      ruleGroups: newGroups,
    });
  };

  // Total de regras
  const totalRules = data.ruleGroups.reduce((sum, group) => sum + group.rules.length, 0);

  return (
    <Box>
      {/* Header com explicação */}
      <RulesInfoBox />

      {/* Alerta especial para Clube Certo */}
      {hasClubeCerto && (
        <Alert
          severity="info"
          sx={{
            mb: 2,
            bgcolor: '#e3f2fd',
            border: '1px solid #2196f3',
            '& .MuiAlert-icon': { color: '#2196f3' }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Regra Exclusiva: Clube Certo</AlertTitle>
          <Typography variant="body2">
            A regra <strong>"Clube Certo"</strong> é uma regra exclusiva e <strong>não pode ser combinada com outros grupos usando E TAMBÉM</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Você ainda pode adicionar outras regras no mesmo grupo usando <strong>OU</strong>, mas não poderá criar grupos adicionais enquanto esta regra estiver ativa.
          </Typography>
        </Alert>
      )}

      {/* Alerta especial para Pix Payment */}
      {hasPixPayment && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            bgcolor: '#fff8e1',
            border: '1px solid #ff9800',
            '& .MuiAlert-icon': { color: '#ff9800' }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Regra Exclusiva: Pagamento Pix</AlertTitle>
          <Typography variant="body2">
            A regra <strong>"Pagamento via Pix"</strong> é uma regra exclusiva. O cliente <strong>compra o brinde pagando via Pix</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Esta regra <strong>não pode ser combinada</strong> com outras regras de abastecimento. Não é possível adicionar mais grupos ou regras.
          </Typography>
        </Alert>
      )}

      {/* Alerta especial para Troca por Pontos */}
      {hasPointsExchange && (
        <Alert
          severity="warning"
          sx={{
            mb: 2,
            bgcolor: '#e0f2f1',
            border: '1px solid #00897b',
            '& .MuiAlert-icon': { color: '#00897b' }
          }}
        >
          <AlertTitle sx={{ fontWeight: 600 }}>Regra Exclusiva: Troca por Pontos</AlertTitle>
          <Typography variant="body2">
            O cliente <strong>paga em pontos</strong>, na tela do sorteio, e recebe na hora os números da sorte deste brinde.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            O preço é do brinde inteiro: <strong>nenhuma outra regra</strong> pode ser adicionada enquanto esta existir.
          </Typography>
        </Alert>
      )}

      {/* Stats */}
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <Chip
          label={`${data.ruleGroups.length} ${data.ruleGroups.length === 1 ? 'Grupo' : 'Grupos'}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`${totalRules} ${totalRules === 1 ? 'Regra' : 'Regras'} no total`}
          color="success"
          variant="outlined"
        />
      </Box>

      {/* Simulação combinada de alcance */}
      <GiftRulesSimulatorPanel ruleGroups={data.ruleGroups} />

      {/* Kanban Board */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          pb: 2,
          minHeight: 200,
        }}
      >
        {data.ruleGroups.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              border: '2px dashed #ccc',
              minHeight: 200,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Nenhum grupo de regras ainda
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Clique no botão abaixo para adicionar seu primeiro grupo
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddGroup}
              size="large"
            >
              Adicionar Primeiro Grupo
            </Button>
          </Paper>
        ) : (
          <>
            {data.ruleGroups.map((group, groupIndex) => (
              <React.Fragment key={group.id}>
                <RuleColumn
                  group={group}
                  groupIndex={groupIndex}
                  canAddRule={!hasPixPayment && !hasUniqueCode && !hasPointsExchange}
                  onAddRule={handleAddRule}
                  onRemoveRule={handleRemoveRule}
                  onRemoveGroup={handleRemoveGroup}
                  fuelsMap={fuelsMap}
                  stationsMap={stationsMap}
                  isOnlyGroup={data.ruleGroups.length === 1}
                />
                {groupIndex < data.ruleGroups.length - 1 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ minWidth: 80 }}
                  >
                    <Chip
                      label="E TAMBÉM"
                      color="primary"
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        height: 32,
                        px: 1,
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            ))}

            {/* Botão para adicionar novo grupo */}
            <Box
              display="flex"
              alignItems="flex-start"
              justifyContent="center"
              sx={{ minWidth: 200 }}
            >
              <Tooltip
                title={
                  !canAddGroup
                    ? 'Não é possível adicionar mais grupos porque existe uma regra exclusiva ativa (Clube Certo ou Pagamento Pix). Estas regras não podem ser combinadas com outros grupos usando E TAMBÉM.'
                    : ''
                }
                placement="top"
                arrow
              >
                <span>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleAddGroup}
                    disabled={!canAddGroup}
                    sx={{
                      height: 'fit-content',
                      borderStyle: 'dashed',
                      borderWidth: 2,
                      py: 3,
                      px: 2,
                      textAlign: 'center',
                    }}
                  >
                    Adicionar Grupo
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </>
        )}
      </Box>

      {/* Modal */}
      <RuleModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setCurrentGroupIndex(null);
        }}
        onSave={handleSaveRule}
        fuels={fuels}
        stations={stations}
        hasMultipleGroups={data.ruleGroups.length > 1}
        hasPixPayment={hasPixPayment}
        hasPointsExchange={hasPointsExchange}
        hasFillinRules={hasFillinRules}
        hasUniqueCode={hasUniqueCode}
        chain={chain}
      />
    </Box>
  );
};
